import config from '../config'
import ApiClient from './ApiClient'
import { Decision, SessionStorageKeys } from '../enums'
import {
  ApiHeaders,
  AssessmentAttempt,
  CategorisationAttempt,
  PaginatedItems,
  Submission,
  SubmissionAnswer,
  Token,
  RiskWarning,
  RiskAcceptance,
} from '../types'

class EnterpriseClient extends ApiClient {
  private async getAccessToken(
    authCode?: string | null,
    codeVerifier?: string | null,
  ) {
    const storedAccessToken = sessionStorage.getItem(
      SessionStorageKeys.AccessToken,
    )

    if (storedAccessToken) {
      return storedAccessToken
    }

    if (!authCode || !codeVerifier) {
      throw new Error('Missing parameters')
    }

    const token = await this.request<Token>('/v1/auth/token', {
      method: 'POST',
      body: {
        grant_type: 'authorization_code',
        code: authCode,
        code_verifier: codeVerifier,
      },
    })

    sessionStorage.setItem(SessionStorageKeys.AccessToken, token.access_token)

    return token.access_token
  }

  private async generateHeaders(): Promise<ApiHeaders> {
    const authCode = sessionStorage.getItem(SessionStorageKeys.AuthCode)
    const codeVerifier = sessionStorage.getItem(SessionStorageKeys.CodeVerifier)

    const accessToken = await this.getAccessToken(authCode, codeVerifier)

    return {
      Authorization: `Bearer ${accessToken}`,
    }
  }

  /* Risk warnings (user-level) */

  public async listRiskWarnings(): Promise<RiskWarning[]> {
    const headers = await this.generateHeaders()

    const response = await this.request<PaginatedItems<RiskWarning>>(
      '/v1/risk-warnings',
      { method: 'GET', headers },
    )

    return response.items
  }

  public async createRiskWarning(): Promise<RiskWarning> {
    const headers = await this.generateHeaders()

    const response = await this.request<RiskWarning>('/v1/risk-warnings', {
      method: 'POST',
      body: {},
      headers,
    })

    sessionStorage.setItem(SessionStorageKeys.RiskWarningId, response.id)

    return response
  }

  public async submitRiskWarning(
    riskWarningId: string,
    {
      decision,
      signedAt,
      learnMoreViewedAt,
    }: { decision: Decision; signedAt: string; learnMoreViewedAt?: string },
  ): Promise<RiskWarning> {
    const headers = await this.generateHeaders()

    return this.request<RiskWarning>(`/v1/risk-warnings/${riskWarningId}`, {
      method: 'POST',
      // `signedAt` is the moment the customer clicked accept/reject (captured
      // by the caller at the event), not the time this request is sent.
      body: { decision, signedAt, learnMoreViewedAt },
      headers,
    })
  }

  /* Risk acceptances (user-level, prompted once the submission is PASSED) */

  public async listRiskAcceptances(): Promise<RiskAcceptance[]> {
    const headers = await this.generateHeaders()

    const response = await this.request<PaginatedItems<RiskAcceptance>>(
      '/v1/risk-acceptances',
      { method: 'GET', headers },
    )

    return response.items
  }

  public async createRiskAcceptance(): Promise<RiskAcceptance> {
    const headers = await this.generateHeaders()

    // 201 fresh, 200 resuming an existing PENDING one — both succeed, so
    // there's nothing to branch on. Empty body: the token supplies the user.
    const response = await this.request<RiskAcceptance>(
      '/v1/risk-acceptances',
      { method: 'POST', body: {}, headers },
    )

    sessionStorage.setItem(SessionStorageKeys.RiskAcceptanceId, response.id)

    return response
  }

  public async submitRiskAcceptance(
    riskAcceptanceId: string,
    { decision, signedAt }: { decision: Decision; signedAt: string },
  ): Promise<RiskAcceptance> {
    const headers = await this.generateHeaders()

    return this.request<RiskAcceptance>(
      `/v1/risk-acceptances/${riskAcceptanceId}`,
      // `signedAt` is the moment the customer clicked accept/reject (captured
      // by the caller at the event); the response returns it as `submittedAt`.
      { method: 'POST', body: { decision, signedAt }, headers },
    )
  }

  /* Submissions */

  public async createSubmission(): Promise<Submission> {
    const headers = await this.generateHeaders()

    const response = await this.request<Submission>('/v1/submissions', {
      method: 'POST',
      body: {},
      headers,
    })

    sessionStorage.setItem(SessionStorageKeys.SubmissionId, response.id)

    return response
  }

  public async listSubmissions(): Promise<Submission[]> {
    const headers = await this.generateHeaders()

    const response = await this.request<PaginatedItems<Submission>>(
      '/v1/submissions',
      { method: 'GET', headers },
    )

    return response.items
  }

  public async getSubmission(submissionId: string): Promise<Submission> {
    const headers = await this.generateHeaders()

    const response = await this.request<Submission>(
      `/v1/submissions/${submissionId}`,
      { method: 'GET', headers },
    )

    sessionStorage.setItem(SessionStorageKeys.SubmissionId, response.id)

    return response
  }

  /* Categorisations */

  public async createCategorisation(
    submissionId: string,
  ): Promise<CategorisationAttempt> {
    const headers = await this.generateHeaders()

    return this.request<CategorisationAttempt>(
      `/v1/submissions/${submissionId}/categorisations`,
      { method: 'POST', headers },
    )
  }

  public async submitCategorisation(
    submissionId: string,
    categorisationId: string,
    answers: SubmissionAnswer[],
    // `signedAt` is when the customer consented (ticked the declaration),
    // captured by the caller at that event — not the time of this request.
    signedAt: string,
  ): Promise<CategorisationAttempt> {
    const headers = await this.generateHeaders()

    return this.request<CategorisationAttempt>(
      `/v1/submissions/${submissionId}/categorisations/${categorisationId}`,
      {
        method: 'POST',
        body: { answers, signedAt, submittedAt: new Date().toISOString() },
        headers,
      },
    )
  }

  /* Assessments */

  public async createAssessment(
    submissionId: string,
  ): Promise<AssessmentAttempt> {
    const headers = await this.generateHeaders()

    return this.request<AssessmentAttempt>(
      `/v1/submissions/${submissionId}/assessments`,
      { method: 'POST', headers },
    )
  }

  public async submitAssessment(
    submissionId: string,
    assessmentId: string,
    answers: SubmissionAnswer[],
    // `signedAt` is when the customer committed to their answers (Submit click),
    // captured by the caller at that event — not the time of this request.
    signedAt: string,
  ): Promise<AssessmentAttempt> {
    const headers = await this.generateHeaders()

    return this.request<AssessmentAttempt>(
      `/v1/submissions/${submissionId}/assessments/${assessmentId}`,
      {
        method: 'POST',
        body: { answers, signedAt, submittedAt: new Date().toISOString() },
        headers,
      },
    )
  }
}

const enterpriseClient = new EnterpriseClient(config.baseUrl)

export default enterpriseClient
export { ApiError } from './ApiClient'
